import { BaseResponseModel } from 'src/app/models/base-response.model';
import {
  ReaErrorData,
  ReaRecoveryAction,
  ReaTechnicalDetailItem,
  ReaValidationErrorDetail,
  ReaImportWarning,
} from 'src/app/models/fincas/rea.model';

import { ReaImportDataPresenter } from './rea-import-data.presenter';

/**
 * Normalizes REA error responses into user-facing recovery data and support details.
 */
export class ReaImportErrorPresenter {
  private static readonly reaAuthorizationGuideUrl =
    'https://locatec.es/proyectos/fitogest/documentos_consulta/guia-subida-autorizacion.pdf';

  public static errorData(response: BaseResponseModel<unknown> | null): ReaErrorData | null {
    const data = response?.firstOrNull();

    return this.isRecord(data) ? data as ReaErrorData : null;
  }

  public static detailedError(response: BaseResponseModel<unknown>): string | null {
    const data = this.errorData(response);
    const userDescription = data?.userMessage?.description?.trim();

    if (userDescription != null && userDescription !== '') {
      return userDescription;
    }

    const directError = data?.errores?.[0]?.descripcion?.trim();

    if (directError != null && directError !== '') {
      return directError;
    }

    const detailedError = data?.details?.errores?.[0]?.descripcion?.trim();

    return detailedError != null && detailedError !== '' ? detailedError : null;
  }

  public static recoveryAction(response: BaseResponseModel<unknown>): ReaRecoveryAction | null {
    const data = this.errorData(response);

    if (data?.recoveryAction != null) {
      return data.recoveryAction;
    }

    return this.isNifCifNotAuthorizedError(data)
      ? this.buildReaAuthorizationRecoveryAction()
      : null;
  }

  public static technicalDetails(response: BaseResponseModel<unknown> | null, statusCode: number | null): ReaTechnicalDetailItem[] {
    const data = this.errorData(response);
    const details = data?.technicalDetails;
    const items: ReaTechnicalDetailItem[] = [];

    this.appendTechnicalDetail(items, 'Código interno', details?.errorCode ?? data?.errorCode);
    this.appendTechnicalDetail(items, 'Estado HTTP', details?.statusCode ?? statusCode);
    this.appendTechnicalDetail(items, 'Trace ID', details?.traceId ?? data?.traceId);
    this.appendTechnicalDetail(items, 'Bridge Trace ID', details?.bridgeTraceId);
    this.appendTechnicalDetail(items, 'NIF/CIF', data?.nif);
    this.appendTechnicalDetail(items, 'Código REA', data?.codigo_REA);
    this.appendTechnicalDetail(items, 'Mensaje servidor', response?.statusMessage);

    this.appendErrorDetails(items, data?.details?.errores ?? data?.errores ?? []);
    this.appendWarnings(items, 'Aviso técnico', data?.technicalWarnings ?? []);
    this.appendWarnings(items, 'Aviso de esquema', data?.schemaWarnings ?? []);

    return items;
  }

  private static appendErrorDetails(items: ReaTechnicalDetailItem[], errors: ReaValidationErrorDetail[]): void {
    errors.forEach((error, index) => {
      const code = String(error.codigo ?? '').trim();
      const description = String(error.descripcion ?? '').trim();
      const label = `Error técnico ${index + 1}`;

      if (code !== '' && description !== '') {
        this.appendTechnicalDetail(items, label, `${code}: ${description}`);
        return;
      }

      this.appendTechnicalDetail(items, label, description !== '' ? description : code);
    });
  }

  private static appendWarnings(items: ReaTechnicalDetailItem[], labelPrefix: string, warnings: ReaImportWarning[]): void {
    warnings.forEach((warning, index) => {
      const value = ReaImportDataPresenter.warningToSupportText(warning);

      this.appendTechnicalDetail(items, `${labelPrefix} ${index + 1}`, value);
    });
  }

  private static isNifCifNotAuthorizedError(data: ReaErrorData | null): boolean {
    if (data?.errorCode !== 'SIEX_BUSINESS_ERROR') {
      return false;
    }

    const errors = data.details?.errores ?? data.errores ?? [];

    return errors.some((error): boolean => {
      const code = String(error.codigo ?? '').trim();
      const description = String(error.descripcion ?? '').trim().toLowerCase();

      return (code === '2' && description.includes('nif/cif no autorizado'))
        || description.includes('nif/cif no autorizado');
    });
  }

  private static buildReaAuthorizationRecoveryAction(): ReaRecoveryAction {
    return {
      type: 'REA_AUTHORIZATION_GUIDE',
      label: 'Ver guía de autorización',
      url: this.reaAuthorizationGuideUrl,
      target: '_blank',
    };
  }

  private static appendTechnicalDetail(items: ReaTechnicalDetailItem[], label: string, value: number | string | null | undefined): void {
    if (value == null || String(value).trim() === '') {
      return;
    }

    items.push({ label, value: String(value) });
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
  }
}
